import "./App.css";
import React, { Suspense, useState, useEffect } from "react";
import { BrowserRouter, Route, Routes, Outlet } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { AuthProvider } from "./contexts/AuthContext";
import Newsletter from "./components/Newsletter";
import ProtectedRoute from "./components/ProtectedRoute";
import Privacy from "./components/pages/Privacy";
import Account from "./components/pages/Account";
import Reserve from "./components/pages/Reserve";
import Checkout from "./components/pages/Checkout";
import ResetPassword from "./components/pages/ResetPassword.jsx";
import PurchaseSuccess from "./components/pages/PurchaseSuccess.jsx";
import ScrollToTop from "./contexts/ScrollToTop.js";
import ColdLead from "./components/pages/ColdLead.jsx";
import OpenApp from "./components/pages/OpenApp.jsx";
import AccountRouter from "./components/pages/AccountRouter.jsx";

const Research = React.lazy(() => import("./components/pages/Research.jsx"));
const AI = React.lazy(() => import("./components/pages/AI"));
const Demo = React.lazy(() => import("./components/pages/Demo"));
const Login = React.lazy(() => import("./components/pages/Login"));
const Signup = React.lazy(() => import("./components/pages/Signup"));
const News = React.lazy(() => import("./components/pages/News"));
const Application = React.lazy(() => import("./components/pages/Application"));
const ComingSoon = React.lazy(() => import("./components/pages/ComingSoon"));
const StudyIntake = React.lazy(() => import("./components/pages/StudyIntake"));
const StudyOuttake = React.lazy(() => import("./components/pages/StudyOuttake"));
const StudySignup = React.lazy(() => import("./components/pages/StudySignup"));
const StudySignupVerify = React.lazy(() => import("./components/pages/StudySignupVerify"));
const ResetPasswordConfirm = React.lazy(() => import("./components/pages/ResetPasswordConfirm"));

function DefaultLayout ({cartItems}) {
  return (
    <>
      <Header cartItems={cartItems}/>
      <Outlet/>
      <Footer/>
    </>
  )
}

function StatsLayout ({cartItems}) {
  return (
    <>
      <Header cartItems={cartItems}/>
      <Outlet/>
      <Footer disclaimer={true} statistics={true} />
    </>
  )
}

function App() {

  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem("qwytt_cart");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem("qwytt_cart", JSON.stringify(cartItems));
  }, [cartItems]);

  // Listen for cart clear events from PurchaseSuccess
  useEffect(() => {
    const handleCartCleared = () => {
      setCartItems({});
    };

    window.addEventListener('cartCleared', handleCartCleared);
    
    return () => {
      window.removeEventListener('cartCleared', handleCartCleared);
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop/>
      <AuthProvider>
        <Suspense
          fallback={
            <div
              style={{
                height: "100vh",
                width: "100vw",
                backgroundColor: "#1A1B1A",
              }}
            >
              <Header></Header>
            </div>
          }
        >
          <Routes>

            <Route element={<DefaultLayout cartItems={cartItems}/>}>
              <Route path="" element={<Application />} />
              <Route path="/ios" element={<ComingSoon />} />
              <Route path="/android" element={<ComingSoon />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/account" element={
                <ProtectedRoute>
                  <AccountRouter />
                </ProtectedRoute>
              } />
              <Route path="/study/intake" element={
                <ProtectedRoute>
                  <StudyIntake />
                </ProtectedRoute>
              } />
              <Route path="/study/outtake" element={
                <ProtectedRoute>
                  <StudyOuttake />
                </ProtectedRoute>
              } />
              <Route path="/study/signup" element={<StudySignup />} />
              <Route path="/study/verify" element={<StudySignupVerify />} />
              <Route path="/open" element={<OpenApp />} />
              <Route path="/login" element={<Login />} />
              <Route path="/learn" element={<ColdLead />} />
              <Route exact path="/news" element={<News />} />
              <Route path="/news/:newsletterId" element={<Newsletter />} />
              <Route path="/reset" element={<ResetPassword />} />
              <Route path="/reset/confirm" element={<ResetPasswordConfirm />} />
              <Route path="/privacy" element={<Privacy />} />
            </Route>

            <Route element={<StatsLayout cartItems={cartItems}/>}>
              <Route path="/research" element={<Research />} />
              <Route path="/reserve/success" element={<PurchaseSuccess />} />
              <Route path="/reserve" element={<Reserve cartItems={cartItems} setCartItems={setCartItems} />}/>
              <Route path="/checkout" element={<Checkout cartItems={cartItems} setCartItems={setCartItems} />}/>
            </Route>
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;